/**
 * cp-charts-bundle — Standalone-Bundle fuer TYPO3-Integration.
 *
 * Pattern: Auto-Hydration (Astro-Islands-Style) statt React-Portal.
 * Fluid-Template rendert nur einen Marker-Div, dieses Bundle scannt das DOM,
 * fetched die Daten via API und mountet pro Marker eine eigene React-Root.
 *
 * Fluid-Template-Beispiel:
 *   <div
 *     class="cp-chart"
 *     data-cp-chart="line"
 *     data-cp-chart-src="/api/charts/{data.uid}"
 *     data-cp-chart-x-key="monat"
 *     data-cp-chart-unit="%"
 *     data-cp-chart-height="320"
 *     data-cp-chart-aria-label="Puenktlichkeit nach Monat"
 *     data-cp-chart-config='{"puenkt":{"label":"Puenktlichkeit","color":"var(--color-chart-blue-100)"}}'
 *   ></div>
 *
 * TYPO3-Backend liefert beim fetch JSON:
 *   [{ "monat": "Jan", "puenkt": 91.2 }, { "monat": "Feb", "puenkt": 90.1 }, ...]
 *
 * Build: pnpm build:charts-bundle
 * Output: public/typo3-bundle/cp-charts-bundle.js (+ .css)
 */

import { createRoot, type Root } from 'react-dom/client';
import {
  CPBarChart,
  CPLineChart,
  CPAreaChart,
  CPPieChart,
  CPRadarChart,
  CPRadialChart,
  CPComposedChart,
} from '../components/Charts';
import '../components/Charts/charts.css';

type ChartType =
  | 'bar' | 'line' | 'area' | 'pie' | 'donut'
  | 'radar' | 'radial' | 'composed';

const HYDRATED_FLAG = 'data-cp-chart-hydrated';

// Roots merken, damit Re-Hydration sauber unmountet
const roots = new WeakMap<HTMLElement, Root>();

function parseList(v: string | undefined): string[] | undefined {
  if (!v) return undefined;
  return v.split(',').map(s => s.trim()).filter(Boolean);
}

function parseBool(v: string | undefined): boolean {
  return v === 'true' || v === '1';
}

function parseInt10(v: string | undefined, fallback: number): number {
  if (!v) return fallback;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Erzwungene Nachkommastellen aus `data-cp-chart-decimals`.
 * "auto" (oder fehlend/ungültig) → undefined = automatische Formatierung
 * (0–2 Stellen). Eine Zahl ("0", "1", "2", …) erzwingt genau so viele Stellen.
 */
function parseDecimals(v: string | undefined): number | undefined {
  if (!v || v.trim().toLowerCase() === 'auto') return undefined;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/**
 * The chart API emits numeric values as strings with a forced single decimal
 * ("9.0", "66.0") so the trailing zero survives JSON (a number can't carry it).
 * Recharts drops non-numeric values from its axis domain, so coerce every
 * value back to a number — except the category columns (xKey/categoryKey, e.g.
 * the year "2021" which stays a category label) and meta keys prefixed "_".
 */
function coerceNumericValues(
  rows: Array<Record<string, unknown>>,
  preserveKeys: ReadonlyArray<string>,
): Array<Record<string, unknown>> {
  const preserve = new Set(preserveKeys);
  return rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(row)) {
      if (preserve.has(key) || key.startsWith('_') || typeof val !== 'string') {
        out[key] = val;
        continue;
      }
      const num = Number(val);
      out[key] = val.trim() !== '' && Number.isFinite(num) ? num : val;
    }
    return out;
  });
}

function showLoading(el: HTMLElement) {
  el.setAttribute('aria-busy', 'true');
  el.innerHTML = '<div class="cp-chart-state cp-chart-state--loading" role="status">Lade Diagramm-Daten ...</div>';
}

function showError(el: HTMLElement, msg: string) {
  el.removeAttribute('aria-busy');
  el.innerHTML = `<div class="cp-chart-state cp-chart-state--error" role="alert">Fehler: ${msg}</div>`;
}

async function hydrate(el: HTMLElement): Promise<void> {
  // Schon hydriert? Skip.
  if (el.hasAttribute(HYDRATED_FLAG)) return;
  el.setAttribute(HYDRATED_FLAG, 'true');

  const type = el.dataset.cpChart as ChartType;
  const src = el.dataset.cpChartSrc;

  if (!type) {
    showError(el, 'data-cp-chart fehlt (line, bar, area, pie, donut, radar, radial, composed)');
    return;
  }
  if (!src) {
    showError(el, 'data-cp-chart-src fehlt (API-URL)');
    return;
  }

  // Config aus inline-JSON ODER aus separatem <script type="application/json">-Child
  let config: Record<string, { label: string; color: string; tooltipLabel?: string }> = {};
  const configInline = el.dataset.cpChartConfig;
  const configScript = el.querySelector<HTMLScriptElement>(
    'script[type="application/json"][data-cp-chart-config]'
  );
  try {
    if (configScript?.textContent) {
      config = JSON.parse(configScript.textContent);
    } else if (configInline) {
      config = JSON.parse(configInline);
    }
  } catch (e) {
    showError(el, 'data-cp-chart-config ist kein gueltiges JSON');
    return;
  }

  const xAxisKey = el.dataset.cpChartXKey ?? 'name';
  const categoryKey = el.dataset.cpChartCategoryKey ?? xAxisKey;
  const unit = el.dataset.cpChartUnit;
  const unitRight = el.dataset.cpChartUnitRight;
  const xUnit = el.dataset.cpChartXUnit;
  const yUnit = el.dataset.cpChartYUnit;
  const yUnitRight = el.dataset.cpChartYUnitRight;
  const height = parseInt10(el.dataset.cpChartHeight, 300);
  const ariaLabel = el.dataset.cpChartAriaLabel;
  const stacked = parseBool(el.dataset.cpChartStacked);
  // Opt-out: default reverses the stack (first series on top). Editors can keep
  // the editor order via the backend checkbox -> data-cp-chart-keep-stack-order.
  const reverseStack = !parseBool(el.dataset.cpChartKeepStackOrder);
  const horizontal = parseBool(el.dataset.cpChartHorizontal);
  const showTotal = parseBool(el.dataset.cpChartShowTotal);
  const showTotalUnit = parseBool(el.dataset.cpChartShowTotalUnit);
  const donut = type === 'donut' || parseBool(el.dataset.cpChartDonut);
  const dualAxis = parseBool(el.dataset.cpChartDualAxis);
  const bars = parseList(el.dataset.cpChartBars);
  const lines = parseList(el.dataset.cpChartLines);
  const decimals = parseDecimals(el.dataset.cpChartDecimals);
  // Category-axis width for horizontal bars (px). 0/absent → CPBarChart default (80).
  const yAxisWidth = parseInt10(el.dataset.cpChartYAxisWidth, 0);
  // Forced step size on the value axis. 0/absent → automatic (Recharts default).
  const valueTickStep = parseInt10(el.dataset.cpChartValueTickStep, 0);

  // Loading
  showLoading(el);

  // Daten holen
  let data: unknown;
  try {
    const res = await fetch(src, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
    data = await res.json();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Netzwerk-Fehler';
    showError(el, `Daten konnten nicht geladen werden (${msg})`);
    console.error('[cp-charts] fetch failed for', src, err);
    return;
  }

  if (!Array.isArray(data)) {
    showError(el, 'API-Response ist kein Array');
    return;
  }

  // Per-point colors for bar charts: the API embeds an `_color` key per row when
  // the editor set `point_color` on the underlying point. Pull them out into a
  // parallel `barColors` array and strip from the data we feed to Recharts so
  // the keys map cleanly to series in the chart config.
  let rows = data as Array<Record<string, unknown>>;
  let barColors: string[] | undefined;
  if (type === 'bar' && rows.some((r) => r && '_color' in r)) {
    barColors = rows.map((r) => (r._color as string) ?? '');
    rows = rows.map(({ _color, ...rest }) => rest);
  }

  // The API ships values as forced-1-decimal strings ("9.0"); Recharts needs
  // real numbers on its value axes. Category columns are preserved as-is.
  rows = coerceNumericValues(rows, [xAxisKey, categoryKey]);

  // React-Root mounten
  el.removeAttribute('aria-busy');
  el.innerHTML = '';
  const root = createRoot(el);
  roots.set(el, root);

  const common = { data: rows as never[], config, height, unit, ariaLabel, decimals };

  switch (type) {
    case 'bar':
      root.render(<CPBarChart {...common} xAxisKey={xAxisKey} stacked={stacked} reverseStack={reverseStack} horizontal={horizontal} showTotal={showTotal} showTotalUnit={showTotalUnit} barColors={barColors} xUnit={xUnit} yUnit={yUnit} yAxisWidth={yAxisWidth > 0 ? yAxisWidth : undefined} valueTickStep={valueTickStep > 0 ? valueTickStep : undefined} />);
      break;
    case 'line':
      root.render(<CPLineChart {...common} xAxisKey={xAxisKey} xUnit={xUnit} yUnit={yUnit} />);
      break;
    case 'area':
      root.render(<CPAreaChart {...common} xAxisKey={xAxisKey} stacked={stacked} xUnit={xUnit} yUnit={yUnit} />);
      break;
    case 'pie':
    case 'donut':
      root.render(<CPPieChart {...common} donut={donut} />);
      break;
    case 'radar':
      root.render(<CPRadarChart {...common} categoryKey={categoryKey} />);
      break;
    case 'radial':
      root.render(<CPRadialChart {...common} />);
      break;
    case 'composed':
      root.render(
        <CPComposedChart
          {...common}
          xAxisKey={xAxisKey}
          bars={bars ?? []}
          lines={lines ?? []}
          dualAxis={dualAxis}
          unitRight={unitRight}
          xUnit={xUnit}
          yUnit={yUnit}
          yUnitRight={yUnitRight}
        />,
      );
      break;
    default:
      showError(el, `Unbekannter Chart-Typ: ${type}`);
  }
}

function init(): void {
  document
    .querySelectorAll<HTMLElement>(`[data-cp-chart]:not([${HYDRATED_FLAG}])`)
    .forEach(hydrate);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Global verfuegbar fuer manuelle Re-Hydration (z.B. nach AJAX-Inhalts-Reload
// im TYPO3-Backend oder bei dynamisch nachgeladenen Elementen).
declare global {
  interface Window {
    cpCharts?: {
      hydrate: () => void;
      hydrateElement: (el: HTMLElement) => Promise<void>;
      unmount: (el: HTMLElement) => void;
    };
  }
}

window.cpCharts = {
  hydrate: init,
  hydrateElement: hydrate,
  unmount: (el: HTMLElement) => {
    const root = roots.get(el);
    if (root) {
      root.unmount();
      roots.delete(el);
      el.removeAttribute(HYDRATED_FLAG);
    }
  },
};
